import React, { useState, useEffect } from "react";
import axios from "axios";
import config from '../config';

import i18n from "../i18n";
import PhoneSettings from './PhoneSettings';
const tr = i18n.t.bind(i18n);
const Settings = () => {

  // All the existing state variables
  const [meats, setMeats] = useState([]);
  const [cooktypes, setCooktypes] = useState([]);
  const [durations, setDurations] = useState([]);
  const [matrix, setMatrix] = useState([]);
  const [bbqs, setBBQs] = useState([]);
  const [newMeat, setNewMeat] = useState({ name: '' });
  const [newCooktype, setNewCooktype] = useState({ name: '' });
  const [newDuration, setNewDuration] = useState({ minutes: '' });
  const [newBBQ, setNewBBQ] = useState({ name: '' });
  const [editMode, setEditMode] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  // Fetch initial data
  useEffect(() => {
    Promise.all([
      axios.get(tr('{{API_BASE_URL}}/meats', {
        API_BASE_URL: config.API_BASE_URL
      })),
      axios.get(tr('{{API_BASE_URL}}/cooktypes', {
        API_BASE_URL: config.API_BASE_URL
      })),
      axios.get(tr('{{API_BASE_URL}}/durations', {
        API_BASE_URL: config.API_BASE_URL
      })),
      axios.get(tr('{{API_BASE_URL}}/bbqs', {
        API_BASE_URL: config.API_BASE_URL
      })),
      axios.get(tr('{{API_BASE_URL}}/matrix', {
        API_BASE_URL: config.API_BASE_URL
      })),
    ])
      .then(([meatsRes, cooktypesRes, durationsRes, bbqsRes, matrixRes]) => {
        setMeats(meatsRes.data);
        setCooktypes(cooktypesRes.data);
        setDurations(durationsRes.data);
        setBBQs(bbqsRes.data);
        setMatrix(matrixRes.data);
      })
      .catch(() => setError('Error loading data.'));
  }, []);

  const handleCreate = (type, data, setData) => {
    const endpoint = tr('{{API_BASE_URL}}/{{type}}', {
      API_BASE_URL: config.API_BASE_URL,
      type
    });
    axios
      .post(endpoint, data)
      .then((response) => {
      const newItem = { id: response.data.id, ...data };
      setData((prev) => [...prev, newItem]);
      setMessage(tr('{{var1}} added successfully.', {
        var1: type.slice(0, -1)
      }));

      // If a new meat was created, populate its cooktypes with defaults
      if (type === 'meats') {
        cooktypes.forEach((cooktype) => {
          if (cooktype.duration) {
            handleMatrixChange(newItem.id, cooktype.id, cooktype.duration);
          }
        });
      }
    })
      .catch(() => {
      return setError(tr('Error adding {{var1}}.', {
        var1: type.slice(0, -1)
      }));
    });
  };

  const handleDelete = (type, id, setData) => {
    const endpoint = tr('{{API_BASE_URL}}/{{type}}/{{id}}', {
      API_BASE_URL: config.API_BASE_URL,
      type,
      id
    });
    axios
      .delete(endpoint)
      .then(() => {
      setData((prev) => prev.filter((item) => item.id !== id));
      setMessage(tr('{{var1}} deleted successfully.', {
        var1: type.slice(0, -1)
      }));
    })
      .catch(() => {
      return setError(tr('Error deleting {{var1}}.', {
        var1: type.slice(0, -1)
      }));
    });
  };

  const handleEdit = (type, item) => {
    setEditMode({ type, id: item.id });
    setEditValue(type === 'durations' ? item.minutes : item.name);
  };

  const handleUpdate = (type, setData) => {
    const endpoint = tr('{{API_BASE_URL}}/{{type}}/{{id}}', {
      API_BASE_URL: config.API_BASE_URL,
      type,
      id: editMode.id
    });
    const updatedValue = type === 'durations' ? { minutes: editValue } : { name: editValue };
    axios
      .put(endpoint, updatedValue)
      .then(() => {
      setData((prev) =>
        prev.map((item) =>
          item.id === editMode.id ? { ...item, ...updatedValue } : item
        )
      );
      setMessage(tr('{{var1}} updated successfully.', {
        var1: type.slice(0, -1)
      }));
      setEditMode(null);
      setEditValue('');
    })
      .catch(() => {
      return setError(tr('Error updating {{var1}}.', {
        var1: type.slice(0, -1)
      }));
    });
  };

  const handleMatrixChange = (meatId, cooktypeId, newDurationId) => {
    // Update the local matrix state
    const updatedMatrix = [...matrix];
    const matrixItem = updatedMatrix.find(
      (item) => item.meat_id === meatId && item.cooktype_id === cooktypeId
    );

    if (matrixItem) {
      // Update existing matrix item
      matrixItem.duration = newDurationId;
    } else {
      // Add a new matrix item
      updatedMatrix.push({
        meat_id: meatId,
        cooktype_id: cooktypeId,
        duration: newDurationId,
      });
    }

    setMatrix(updatedMatrix);

    // Update the backend
    axios
      .post(tr('{{API_BASE_URL}}/matrix', {
      API_BASE_URL: config.API_BASE_URL
    }), {
        meat: meatId,
        cooktype: cooktypeId,
        duration: newDurationId,
      })
      .then(() => setMessage('Matrix updated successfully.'))
      .catch(() => setError('Error updating matrix.'));
  };

  const handleCooktypeDurationChange = (cooktypeId, newDurationId) => {
    // Update cooktype in backend
    axios
      .put(tr('{{API_BASE_URL}}/cooktypes/{{cooktypeId}}/duration', {
      API_BASE_URL: config.API_BASE_URL,
      cooktypeId
    }), {
        duration: newDurationId,
      })
      .then(() => {
        // Update local cooktypes state
        setCooktypes((prev) =>
          prev.map((c) =>
            c.id === cooktypeId ? { ...c, duration: newDurationId } : c
          )
        );
        setMessage('Cooktype duration updated successfully.');
  
        // Update all cells in the matrix for this cooktype
        setMatrix((prev) =>
          prev.map((item) =>
            item.cooktype_id === cooktypeId
              ? { ...item, duration: newDurationId }
              : item
          )
        );
  
        // Optionally sync with backend matrix for all meats
        meats.forEach((meat) => {
          handleMatrixChange(meat.id, cooktypeId, newDurationId);
        });
      })
      .catch(() => setError('Error updating cooktype duration.'));
  };
  
  
  const saveMeatDefaults = async (meatId, { default_quantity, default_cooktype }) => {
    try {
      await axios.put(
        tr('{{API_BASE_URL}}/meats/{{id}}/defaults', {
          API_BASE_URL: config.API_BASE_URL,
          id: meatId
        }),
        { default_quantity, default_cooktype }
      );
      setMessage(tr('Meat defaults updated successfully.'));
      setError('');
    } catch (e) {
      setError(tr('Error updating meat defaults.'));
    }
  };  

  const compactSelectStyle = {
    height: 22,
    whiteSpace: 'nowrap'
  };
  
  const compactInputStyle = {
    width: 50,
    height: 15,
    textAlign: 'center',
    whiteSpace: 'nowrap'
  };
  
  const cellBaseStyle = {
    height: 24,
    padding: '2px 4px',
    whiteSpace: 'nowrap'
  };    


  return (
    <div style={{ width: '95%', margin: 'auto' }}>
      <h2>{tr('General Settings')}</h2>
      {error && <p style={{ color: 'red' }}>{error}</p>}
      {message && <p style={{ color: 'green' }}>{message}</p>}
      {/* Matrix Section */}
      <div
        style={{
          width: '95%',
          maxWidth: '1400px',
          margin: '20px auto',
          textAlign: 'center',
          padding: '10px 14px',
          overflowX: 'auto'
        }}
      >
        <h3>{tr('Cook-Types Matrix & Defaults')}</h3>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `320px minmax(60px, max-content) minmax(110px, max-content) repeat(${cooktypes.length}, 1fr)`,
            alignItems: 'center',
            width: 'max-content',
            margin: '0 auto'
          }}
        >
          {/* Row 1: Default duration header (now needs 2 extra empty cells) */}
          <div></div>
          
          <b
            style={{
              gridColumn: '2 / span 2',
              textAlign: 'center'
            }}
          >
            {tr('Default Values:')}
          </b>
          {cooktypes.map((cooktype) => (
            <div key={`dropdown-${cooktype.id}`}>
              <select
                value={cooktype.duration || ''}
                onChange={(e) => handleCooktypeDurationChange(cooktype.id, e.target.value)}
              >
                <option value="">{tr('None')}</option>
                {durations.map((duration) => (
                  <option key={duration.id} value={duration.id}>
                    {duration.minutes}{tr("'")}
                  </option>
                ))}
              </select>
            </div>
          ))}
        
          {/* Row 2: Column headers */}
          <div></div>
          <div><b>{tr('Quantity')}</b></div>
          <div><b>{tr('Cook Type')}</b></div>
          {cooktypes.map((cooktype) => (
            <div key={cooktype.id}>
              <b style={{ color: 'darkBlue' }}>{cooktype.name}</b>
            </div>
          ))}
        
          {/* Rows: meats */}
          {meats.map((meat, rowIndex) => {
            const rowBg = rowIndex % 2 === 0 ? '#eaf4f7' : 'transparent';
            const rowCellStyle = {
              ...cellBaseStyle,
              backgroundColor: rowBg
            };
          
            return (
              <React.Fragment key={meat.id}>
                {/* Meat name */}
                <div style={{ ...rowCellStyle, textAlign: 'right' }}>
                  <b style={{ color: 'darkBlue' }}>{meat.name}</b>
                </div>
          
                {/* Default quantity */}
                <div style={{ ...rowCellStyle, textAlign: 'center' }}>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    style={compactInputStyle}
                    value={meat.default_quantity ?? ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      setMeats((prev) =>
                        prev.map((m) => (m.id === meat.id ? { ...m, default_quantity: v } : m))
                      );
                    }}
                    onBlur={(e) => {
                      const raw = e.target.value;
                      const q = raw === '' ? null : Number(raw);
                      if (q !== null && (!Number.isInteger(q) || q <= 0)) {
                        setError(tr('Quantity must be a positive number.'));
                        return;
                      }
          
                      const ct = meat.default_cooktype ?? null;
                      saveMeatDefaults(meat.id, { default_quantity: q, default_cooktype: ct });
                    }}
                  />
                </div>
          
                {/* Default cooktype */}
                <div style={{ ...rowCellStyle, textAlign: 'center' }}>
                  <select
                    style={compactSelectStyle}
                    value={meat.default_cooktype ?? ''}
                    onChange={(e) => {
                      const nextCooktype = e.target.value === '' ? null : Number(e.target.value);
          
                      // update UI immediately
                      setMeats((prev) =>
                        prev.map((m) => (m.id === meat.id ? { ...m, default_cooktype: nextCooktype } : m))
                      );
          
                      // persist (use current quantity from state)
                      const qRaw = meat.default_quantity;
                      const q =
                        qRaw === '' || qRaw === undefined || qRaw === null ? null : Number(qRaw);
          
                      saveMeatDefaults(meat.id, { default_quantity: q, default_cooktype: nextCooktype });
                    }}
                  >
                    <option value="">{tr('None')}</option>
                    {cooktypes.map((ct) => (
                      <option key={ct.id} value={ct.id}>
                        {ct.name}
                      </option>
                    ))}
                  </select>
                </div>
          
                {/* Matrix cells (unchanged) */}
                {cooktypes.map((cooktype) => {
                  const cell = matrix.find(
                    (item) => item.meat_id === meat.id && item.cooktype_id === cooktype.id
                  );
          
                  return (
                    <div key={`${meat.id}-${cooktype.id}`} style={rowCellStyle}>
                      <select
                        style={compactSelectStyle}
                        value={cell?.duration || ''}
                        onChange={(e) => handleMatrixChange(meat.id, cooktype.id, e.target.value)}
                      >
                        <option value="">{tr('None')}</option>
                        {durations.map((duration) => (
                          <option key={duration.id} value={duration.id}>
                            {duration.minutes}{tr("'")}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </React.Fragment>
            );
          })}
        </div>

        <br/>
        <hr/>
      </div>
      <br/>
      {/* CRUD Sections */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px'}}>
        {/* First Row */}
        <div style={{marginLeft: '35%', width: '450px'}}>
          <h3>{tr('Manage Meats')}</h3>
          <input
            placeholder={tr('New Meat Name')}
            value={newMeat.name}
            onChange={(e) => setNewMeat({ name: e.target.value })}
          />
          <button onClick={() => handleCreate('meats', newMeat, setMeats)} style={{marginLeft: '20px'}}>{tr('Add')}</button>
          <ul>
            {meats.map(meat => {
              return (
                <li key={meat.id} style={{ marginBottom: '10px' }}>
                  {editMode?.id === meat.id ? (
                    <>
                      <input
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                      />
                      <button style={{float: 'right', marginLeft: '10px'}} onClick={() => setEditMode(null)}>{tr('Cancel')}</button>
                      <button style={{float: 'right', marginLeft: '10px'}} onClick={() => handleUpdate('meats', setMeats)}>{tr('Save')}</button>
                    </>
                  ) : (
                    <>
                      <i style={{color: 'darkBlue'}}>{meat.name}</i>
                      <button style={{float: 'right', marginLeft: '10px'}} onClick={() => handleDelete('meats', meat.id, setMeats)}>{tr('Delete')}</button>
                      <button style={{float: 'right', marginLeft: '10px'}} onClick={() => handleEdit('meats', meat)}>{tr('Edit')}</button>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        </div>

        <div style={{marginLeft: '20%', width: '300px'}}>
          <h3>{tr('Manage Cooktypes')}</h3>
          <input
            placeholder={tr('New Cooktype Name')}
            value={newCooktype.name}
            onChange={(e) => setNewCooktype({ name: e.target.value })}
          />
          <button onClick={() => handleCreate('cooktypes', newCooktype, setCooktypes)} style={{marginLeft: '20px'}}>{tr('Add')}</button>
          <ul>
            {cooktypes.map(cooktype => {
              return (
                <li key={cooktype.id} style={{ marginBottom: '10px' }}>
                  {editMode?.id === cooktype.id ? (
                    <>
                      <input
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                      />
                      <button style={{float: 'right', marginLeft: '10px'}} onClick={() => setEditMode(null)}>{tr('Cancel')}</button>
                      <button style={{float: 'right', marginLeft: '10px'}} onClick={() => handleUpdate('cooktypes', setCooktypes)}>{tr('Save')}</button>
                    </>
                  ) : (
                    <>
                      <i style={{color: 'darkBlue'}}>{cooktype.name}</i>
                      <button style={{float: 'right', marginLeft: '10px'}} onClick={() => handleDelete('cooktypes', cooktype.id, setCooktypes)}>{tr('Delete')}</button>
                      <button style={{float: 'right', marginLeft: '10px'}} onClick={() => handleEdit('cooktypes', cooktype)}>{tr('Edit')}</button>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        </div>

        {/* Second Row */}
        <div style={{marginLeft: '35%', width: '450px'}}>
          <h3>{tr('Manage Durations')}</h3>
          <input
            placeholder={tr('New Duration (minutes)')}
            type="number"
            value={newDuration.minutes}
            onChange={(e) => setNewDuration({ minutes: e.target.value })}
          />
          <button onClick={() => handleCreate('durations', newDuration, setDurations)} style={{marginLeft: '20px'}}>{tr('Add')}</button>
          <ul>
            {durations.map(duration => {
              return (
                <li key={duration.id} style={{ marginBottom: '10px' }}>
                  {editMode?.id === duration.id ? (
                    <>
                      <input
                        type="number"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                      />
                      <button style={{float: 'right', marginLeft: '10px'}} onClick={() => setEditMode(null)}>{tr('Cancel')}</button>
                      <button style={{float: 'right', marginLeft: '10px'}} onClick={() => handleUpdate('durations', setDurations)}>{tr('Save')}</button>
                    </>
                  ) : (
                    <>
                      <i style={{color: 'darkBlue'}}>{duration.minutes}{tr("'")}</i>
                      <button style={{float: 'right', marginLeft: '10px'}} onClick={() => handleDelete('durations', duration.id, setDurations)}>{tr('Delete')}</button>
                      <button style={{float: 'right', marginLeft: '10px'}} onClick={() => handleEdit('durations', duration)}>{tr('Edit')}</button>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        </div>

        <div style={{marginLeft: '20%', width: '300px'}}>
          <h3>{tr('Manage BBQs')}</h3>
          <input
            placeholder={tr('New BBQ Name')}
            value={newBBQ.name}
            onChange={(e) => setNewBBQ({ name: e.target.value })}
          />
          <button onClick={() => handleCreate('bbqs', newBBQ, setBBQs)} style={{marginLeft: '20px'}}>{tr('Add')}</button>
          <ul>
            {bbqs.map(bbq => {
              return (
                <li key={bbq.id} style={{ marginBottom: '10px' }}>
                  {editMode?.id === bbq.id ? (
                    <>
                      <input
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                      />
                      <button style={{float: 'right', marginLeft: '10px'}} onClick={() => setEditMode(null)}>{tr('Cancel')}</button>
                      <button style={{float: 'right', marginLeft: '10px'}} onClick={() => handleUpdate('bbqs', setBBQs)}>{tr('Save')}</button>
                    </>
                  ) : (
                    <>
                      <i style={{color: 'darkBlue'}}>{bbq.name}</i>
                      <button style={{float: 'right', marginLeft: '10px'}} onClick={() => handleDelete('bbqs', bbq.id, setBBQs)}>{tr('Delete')}</button>
                      <button style={{float: 'right', marginLeft: '10px'}} onClick={() => handleEdit('bbqs', bbq)}>{tr('Edit')}</button>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
        
      </div>
      {/* Αναγνώριση κλήσης (Yealink) — ρυθμίσεις & δοκιμή */}
      <PhoneSettings />
      <br/><br/>
    </div>
  );
};

export default Settings;
